import {
  DateQuestionModeType,
  DateValidationType,
  RangeConditionType,
} from '../dateValidation/types';
import { getOffsetUnit, takesNoOffset, takesTwoOffsets } from './comparisons';
import { getOffsetBounds } from './constants';

/**
 * Converts days to hours (multiplies by 24).
 * Use this when dateMode is 'date' to convert display value to internal storage format.
 */
export const convertToHoursFromDays = (days: string): string => {
  const daysNumber = Number(days);
  return (daysNumber * 24).toString();
};

/**
 * Converts hours to days (divides by 24).
 * Use this when dateMode is 'date' to convert internal storage format to display value.
 */
export const convertToDaysFromHours = (hours: string): string => {
  return (Number(hours) / 24).toString();
};

/**
 * Converts an internal offset in hours (stored format) to a display offset value.
 * - In 'date' mode: converts hours to days.
 * - In 'datetime' or 'time' mode: returns the hours value as-is.
 *
 */
export const convertOffsetHoursToDisplayOffsetValue = (
  hours: string,
  dateMode: DateQuestionModeType,
): string => {
  if (!hours || hours === '') {
    return '';
  }
  return getOffsetUnit(dateMode) === 'days' ? convertToDaysFromHours(hours) : hours;
};

/**
 * Converts a display offset value to the internal offset in hours (stored format).
 * - In 'date' mode: converts days to hours.
 * - In 'datetime' or 'time' mode: returns the hours value as-is.
 */
export const convertDisplayOffsetValueToOffsetHours = (
  value: string,
  dateMode: DateQuestionModeType,
): string => {
  if (!value || value === '') {
    return '';
  }
  return getOffsetUnit(dateMode) === 'days' ? convertToHoursFromDays(value) : value;
};

/**
 * `notBetween` in `time` mode is unanswerable when the excluded window covers every time of
 * day: the raw window `[t - left, t + right]` is `left + right` hours wide for every answering
 * time `t`, and clamping it to a single day can only shrink it, never grow it back out. So
 * `left + right >= 24` covers every time of day at every answering time (unanswerable), while
 * `left + right < 24` always leaves at least one time of day outside it (answerable) - the
 * criterion is exact, not a heuristic, and both values are known at build time (DS-4175 review).
 */
export const isUnsatisfiableNotBetweenTimeWindow = (
  selectedRangeCondition: RangeConditionType,
  dateMode: DateQuestionModeType,
  isInvalidOffsetToLeftValue: boolean,
  isInvalidOffsetToRightValue: boolean,
  offsetHoursToLeftValue: number,
  offsetHoursToRightValue: number,
): boolean =>
  selectedRangeCondition === 'notBetween' &&
  dateMode === 'time' &&
  !isInvalidOffsetToLeftValue &&
  !isInvalidOffsetToRightValue &&
  offsetHoursToLeftValue + offsetHoursToRightValue >= 24;

export interface HasDateValidationErrorsOptions {
  selectedRangeCondition: RangeConditionType;
  dateMode: DateQuestionModeType;
  offsetHours?: string;
  offsetHoursToLeft?: string;
  offsetHoursToRight?: string;
  dateValidationType?: DateValidationType | '';
}

/**
 * Named fields, not positional params: `80b1482ad` fixed a bug whose entire cause was a call
 * site that omitted the trailing `dateValidationType` argument to `getOffsetBounds`, silently
 * validating a relative question against the fixed mode's 24h floor. Five optional positional
 * params with the one that decides which rule set applies last reproduces the same hazard one
 * layer up — nothing in the type system catches the next call site that forgets it, where a
 * missing field here is a compile error instead (DS-4176 review).
 */
export const hasDateValidationErrors = ({
  selectedRangeCondition,
  dateMode,
  offsetHours,
  offsetHoursToLeft,
  offsetHoursToRight,
  dateValidationType,
}: HasDateValidationErrorsOptions): boolean => {
  if (!selectedRangeCondition) return false;
  if (takesNoOffset(selectedRangeCondition)) {
    return false;
  }
  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';
  const { min: minOffsetAllowedHours, max: maxOffsetHoursAllowedHours } = getOffsetBounds(
    safeDateMode,
    dateValidationType,
  );

  const minOffsetAllowedValue = Number(
    convertOffsetHoursToDisplayOffsetValue(minOffsetAllowedHours.toString(), safeDateMode),
  );
  const maxOffsetAllowedValue =
    maxOffsetHoursAllowedHours !== null
      ? Number(
          convertOffsetHoursToDisplayOffsetValue(
            maxOffsetHoursAllowedHours.toString(),
            safeDateMode,
          ),
        )
      : null;

  if (takesTwoOffsets(selectedRangeCondition)) {
    const offsetHoursToLeftValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToLeft || '', safeDateMode),
    );
    const offsetHoursToRightValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToRight || '', safeDateMode),
    );

    const isInvalidOffsetToLeftValue =
      isNaN(offsetHoursToLeftValue) ||
      offsetHoursToLeftValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetHoursToLeftValue > maxOffsetAllowedValue);
    const isInvalidOffsetToRightValue =
      isNaN(offsetHoursToRightValue) ||
      offsetHoursToRightValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetHoursToRightValue > maxOffsetAllowedValue);

    return (
      isInvalidOffsetToLeftValue ||
      isInvalidOffsetToRightValue ||
      isUnsatisfiableNotBetweenTimeWindow(
        selectedRangeCondition,
        safeDateMode,
        isInvalidOffsetToLeftValue,
        isInvalidOffsetToRightValue,
        offsetHoursToLeftValue,
        offsetHoursToRightValue,
      )
    );
  }

  const offsetValue = Number(
    convertOffsetHoursToDisplayOffsetValue(offsetHours || '', safeDateMode),
  );
  const isInvalidOffsetValue =
    isNaN(offsetValue) ||
    offsetValue < minOffsetAllowedValue ||
    (maxOffsetAllowedValue !== null && offsetValue > maxOffsetAllowedValue);

  return isInvalidOffsetValue;
};

export interface ShouldKeepDateValidationFieldsOptions {
  selectedRangeCondition: RangeConditionType;
  dateMode: DateQuestionModeType;
  dateValidationType?: DateValidationType | '';
  offsetHours?: string;
  offsetHoursToLeft?: string;
  offsetHoursToRight?: string;
  referenceQuestionId?: string;
  referenceQuestionIdToLeft?: string;
  referenceQuestionIdToRight?: string;
}

/**
 * Whether a `date` question's range-condition configuration is complete enough to persist
 * as-is. This is the save-time counterpart of `hasDateValidationErrors` (the builder's inline
 * field error and hint banner). Before DS-4175's review it had its own separate, narrower
 * reimplementation in `formBuilder.tsx` that only checked each offset's own range and never
 * consulted the unanswerable-window case, so an admin could save past the panel's warning and
 * leave a `notBetween` `time` question no responder could ever answer. Routing the save-time
 * decision through the same predicate the panel already shows closes that gap by construction:
 * anything the panel flags as an error is now also rejected here.
 *
 * Extended for relative mode (DS-4176): `hasDateValidationErrors` only knows about offset
 * bounds, so it has no way to catch a relative comparison with a chosen offset but no reference
 * question to compare against (a two-sided relative comparison has no offset to check at all —
 * it needs both references instead). That requirement is checked here, ahead of the shared
 * offset check, using the exact same field names merge conflicts against this file keep
 * colliding on (DS-4176 review, DS-4175 thread from 2026-09-09: keep this version, not a
 * simpler positional one that drops it).
 */
export const shouldKeepDateValidationFields = ({
  selectedRangeCondition,
  dateMode,
  dateValidationType,
  offsetHours,
  offsetHoursToLeft,
  offsetHoursToRight,
  referenceQuestionId,
  referenceQuestionIdToLeft,
  referenceQuestionIdToRight,
}: ShouldKeepDateValidationFieldsOptions): boolean => {
  if (!selectedRangeCondition) return false;
  if (takesNoOffset(selectedRangeCondition)) return true;

  if (dateValidationType === 'relativeRange') {
    const hasRequiredReferenceFields = takesTwoOffsets(selectedRangeCondition)
      ? !!referenceQuestionIdToLeft && !!referenceQuestionIdToRight
      : !!referenceQuestionId;
    if (!hasRequiredReferenceFields) return false;
    // A two-sided relative comparison takes two reference questions and no offset at all
    // (the panel never renders one for it), so there is nothing left to check.
    if (takesTwoOffsets(selectedRangeCondition)) return true;
  }

  return !hasDateValidationErrors({
    selectedRangeCondition,
    dateMode,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    dateValidationType,
  });
};
